import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLiveSync } from "@/lib/useLiveSync";
import { Card } from "@/components/ui/card";
import Logo from "@/components/Logo";
import { ArrowLeft, ArrowRight, FileCode, FileText, Sparkles } from "lucide-react";
import DemoBanner from "@/components/DemoBanner";

export default function LearnerCourse() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);

  const reload = useCallback(() => {
    Promise.all([api.get(`/courses/${courseId}`), api.get(`/modules?course_id=${courseId}`)])
      .then(([c, m]) => { setCourse(c.data); setModules(m.data); });
  }, [courseId]);

  useEffect(() => { reload(); }, [reload]);

  // Live admin-edit sync: any module add/delete/rename/reorder/content change
  // in this course refreshes the list instantly.
  useLiveSync({ kind: "course", id: courseId, onUpdate: reload });

  if (!course) return <div className="p-10 text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <DemoBanner />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/learn" className="text-sm text-slate-600 hover:text-blue-900 inline-flex items-center gap-1.5">
            <ArrowLeft size={14}/> All Courses
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Course</div>
          <h1 className="text-4xl font-display font-bold text-slate-900 tracking-tight">{course.title}</h1>
          <p className="text-slate-500 mt-3 max-w-2xl">{course.description || "Begin with the first module below."}</p>
        </div>

        {modules.length === 0 ? (
          <Card className="p-16 text-center border-dashed">
            <Sparkles size={36} className="mx-auto text-slate-300 mb-3" strokeWidth={1.3}/>
            <div className="text-slate-700 font-medium">No published modules yet</div>
          </Card>
        ) : (
          <div className="space-y-3">
            {modules.map((m, i) => (
              <Link key={m.id} to={`/learn/modules/${m.id}`} className="block group" data-testid={`learner-module-${m.id}`}>
                <Card className="p-5 border-slate-200 group-hover:border-blue-900/40 group-hover:shadow-md transition-all">
                  <div className="flex items-center gap-5">
                    <div className="w-12 h-12 rounded-md bg-blue-900 text-white font-display font-bold flex items-center justify-center shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-semibold text-slate-900 group-hover:text-blue-900 transition-colors">{m.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="inline-flex items-center gap-1">
                          {m.artifact_type === "html" ? <FileCode size={12}/> : <FileText size={12}/>}
                          {m.artifact_type === "html" ? "Interactive Lesson" : "Reading + Quiz"}
                        </span>
                      </div>
                    </div>
                    <ArrowRight size={18} className="text-slate-400 group-hover:text-blue-900 group-hover:translate-x-0.5 transition-all"/>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
